import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button, Spin, Typography, message, Space } from 'antd';
import { SaveOutlined, CalculatorOutlined } from '@ant-design/icons';
import { HotTable } from '@handsontable/react';
import { registerAllModules } from 'handsontable/registry';
import Handsontable from 'handsontable';
import dayjs from 'dayjs';
import 'handsontable/dist/handsontable.full.css';
import api from '../../services/api';

// Tüm modülleri kaydet
registerAllModules();

const { Text } = Typography;

const ForecastTable = ({ 
    selectedTemplate, 
    targetMonth, 
    onDataChange, 
    onBulkCalculate 
}) => {
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [calculating, setCalculating] = useState(false);
    const [brands, setBrands] = useState([]);
    const hotTableRef = useRef(null);
    const targetMonthFormatted = targetMonth || dayjs().add(1, 'month').format('YYYY-MM');

    // Tablo verilerini yükle
    const loadTableData = async () => {
        try {
            setLoading(true);
            
            // Forecast ayarları ve sonuçları paralel olarak al
            const forecastUrl = '/forecast';
            const resultsUrl = selectedTemplate 
                ? `/forecast/results?targetMonth=${targetMonthFormatted}&template=${selectedTemplate}`
                : `/forecast/results?targetMonth=${targetMonthFormatted}`;
                
            const [settingsResponse, resultsResponse] = await Promise.all([
                api.get(forecastUrl),
                api.get(resultsUrl)
            ]);

            const settings = settingsResponse.data || [];
            const results = resultsResponse.data || [];

            // Template filtreleme uygula
            let filteredSettings = settings;
            if (selectedTemplate) {
                filteredSettings = settings.filter(setting => {
                    if (setting.brandId && setting.Brand) {
                        return setting.Brand.template === selectedTemplate;
                    }
                    return false;
                });
            }

            // Markalar ve forecast verilerini birleştir
            const brandsWithForecast = [];
            const brandsList = [];

            filteredSettings.forEach(setting => {
                if (setting.Brand) {
                    const existingResult = results.find(r => r.brandId === setting.brandId);
                    
                    brandsWithForecast.push({
                        brandId: setting.Brand.id,
                        brandName: setting.Brand.name,
                        currency: setting.Brand.currency || 'TRY',
                        template: setting.Brand.template,
                        forecastValue: existingResult?.forecastValue || null,
                        aysonuTahminiCiro: existingResult?.aysonuTahminiCiro || null,
                        tahminiDegisim: existingResult?.tahminiDegisim || null,
                        tahminiDegisim11: existingResult?.tahminiDegisim11 || null,
                        hedefCiro: existingResult?.hedefCiro || null,
                        hedefCiroMetricId: existingResult?.hedefCiroMetricId || null,
                        hedefROI: existingResult?.hedefROI || null,
                        hedefROIMetricId: existingResult?.hedefROIMetricId || null,
                        hasSettings: true,
                        settingId: setting.id,
                        formula: setting.formula,
                        autoSave: setting.autoSave,
                        lastCalculated: existingResult?.calculatedAt || null
                    });

                    brandsList.push(setting.Brand);
                }
            });

            setBrands(brandsList);
            setTableData(brandsWithForecast);
            
        } catch (error) {
            console.error('Tablo verisi yüklenirken hata:', error);
            console.error('Error details:', {
                status: error.response?.status,
                data: error.response?.data,
                message: error.message
            });
            message.error(`Tablo verisi yüklenirken bir hata oluştu: ${error.response?.data?.error || error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Toplu hesaplama
    const handleBulkCalculate = async () => {
        try {
            setCalculating(true);
            
            const payload = {
                targetMonth: targetMonthFormatted
            };
            
            if (selectedTemplate) {
                payload.template = selectedTemplate;
            }

            const response = await api.post('/forecast/calculate/bulk', payload);
            
            if (response.data.success) {
                message.success(response.data.message);
                
                // Tabloyu yenile
                await loadTableData();
                
                if (onBulkCalculate) {
                    onBulkCalculate(response.data.results);
                }
            }
        } catch (error) {
            console.error('Toplu hesaplama hatası:', error);
            message.error('Toplu hesaplama sırasında hata oluştu: ' + (error.response?.data?.error || error.message));
        } finally {
            setCalculating(false);
        }
    };

    // Manuel forecast değeri güncelle
    const updateForecastValue = async (brandId, newValue) => {
        try {
            await api.post(`/forecast/brands/${brandId}/calculate`, {
                targetMonth: targetMonthFormatted,
                manualOverride: true,
                forecastValue: newValue
            });

            message.success('Forecast değeri güncellendi');
        } catch (error) {
            console.error('Manuel güncelleme hatası:', error);
            message.error('Değer güncellenirken hata oluştu');
        }
    };

    // HEDEF CİRO değerini güncelle
    const updateHedefCiro = async (brandId, newValue, hedefCiroMetricId) => {
        try {
            if (!hedefCiroMetricId) {
                message.error('Bu marka için HEDEF CİRO metriği bulunamadı');
                return;
            }

            const nextMonth = dayjs(targetMonthFormatted).format('YYYY-MM');

            await api.post(`/monthly-metrics/${hedefCiroMetricId}/brands/${brandId}/data`, {
                date: nextMonth,
                value: newValue
            });

            message.success('HEDEF CİRO güncellendi');
        } catch (error) {
            console.error('HEDEF CİRO güncelleme hatası:', error);
            message.error('HEDEF CİRO güncellenirken hata oluştu');
        }
    };

    // HEDEF ROI değerini güncelle
    const updateHedefROI = async (brandId, newValue, hedefROIMetricId) => {
        try {
            if (!hedefROIMetricId) {
                message.error('Bu marka için HEDEF ROI metriği bulunamadı');
                return;
            }

            const nextMonth = dayjs(targetMonthFormatted).format('YYYY-MM');

            await api.post(`/monthly-metrics/${hedefROIMetricId}/brands/${brandId}/data`, {
                date: nextMonth,
                value: newValue
            });

            message.success('HEDEF ROI güncellendi');
        } catch (error) {
            console.error('HEDEF ROI güncelleme hatası:', error);
            message.error('HEDEF ROI güncellenirken hata oluştu');
        }
    };


    useEffect(() => {
        loadTableData();
    }, [selectedTemplate, targetMonthFormatted]);

    // Handsontable ayarları
    const hotSettings = useMemo(() => {
        const columns = [
            {
                title: 'Marka',
                data: 'brandName',
                readOnly: true,
                className: 'brand-name-col',
                width: 180
            },
            {
                title: `AY SONU<br/>TAHMİNİ CİRO<br/>${dayjs(targetMonthFormatted).subtract(1, 'month').format('MMM YYYY')}`,
                data: 'aysonuTahminiCiro',
                readOnly: true,
                type: 'numeric',
                className: 'aysonu-col',
                width: 160,
                renderer: (instance, td, row, col, prop, value, cellProperties) => {
                    const brand = tableData[row];
                    if (brand) {
                        const formattedValue = value ? 
                            `${parseFloat(value).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ${brand.currency}` 
                            : '-';
                        
                        td.innerHTML = formattedValue;
                        
                        if (value) {
                            td.style.backgroundColor = '#e6f7ff';
                            td.style.color = '#1890ff';
                        } else {
                            td.style.color = '#ccc';
                        }
                    }
                    return td;
                }
            },
            {
                title: `ÖNCEKİ AYA<br/>GÖRE TAHMİNİ<br/>DEĞİŞİM ${dayjs().format('MMM YYYY')}`,
                data: 'tahminiDegisim',
                readOnly: true,
                type: 'numeric',
                className: 'degisim-col',
                width: 180,
                renderer: (instance, td, row, col, prop, value, cellProperties) => {
                    if (value) {
                        const numValue = parseFloat(value);
                        const formattedValue = `%${numValue.toFixed(2)}`;
                        td.innerHTML = formattedValue;
                        
                        // Pozitif/negatif renk
                        if (numValue > 0) {
                            td.style.backgroundColor = '#f6ffed';
                            td.style.color = '#52c41a';
                            td.style.fontWeight = 'bold';
                        } else if (numValue < 0) {
                            td.style.backgroundColor = '#fff2f0';
                            td.style.color = '#ff4d4f';
                            td.style.fontWeight = 'bold';
                        } else {
                            td.style.backgroundColor = '#fafafa';
                            td.style.color = '#666';
                        }
                    } else {
                        td.innerHTML = '-';
                        td.style.color = '#ccc';
                    }
                    return td;
                }
            },
            {
                title: `11 AY<br/>ÖNCEKİ DEĞİŞİM<br/>${dayjs().subtract(11, 'month').format('MMM YYYY')}`,
                data: 'tahminiDegisim11',
                readOnly: true,
                type: 'numeric',
                className: 'degisim11-col',
                width: 160,
                renderer: (instance, td, row, col, prop, value, cellProperties) => {
                    if (value) {
                        const numValue = parseFloat(value);
                        const formattedValue = `%${numValue.toFixed(2)}`;
                        td.innerHTML = formattedValue;
                        
                        // Pozitif/negatif renk (daha açık tonlar)
                        if (numValue > 0) {
                            td.style.backgroundColor = '#f9ffed';
                            td.style.color = '#7cb305';
                            td.style.fontWeight = 'bold';
                        } else if (numValue < 0) {
                            td.style.backgroundColor = '#fff7f0';
                            td.style.color = '#fa541c';
                            td.style.fontWeight = 'bold';
                        } else {
                            td.style.backgroundColor = '#fafafa';
                            td.style.color = '#999';
                        }
                    } else {
                        td.innerHTML = '-';
                        td.style.color = '#ccc';
                    }
                    return td;
                }
            },
            {
                title: `Forecast ${dayjs(targetMonthFormatted).format('MMM YYYY')}`,
                data: 'forecastValue',
                type: 'numeric',
                numericFormat: {
                    pattern: '0,0.00'
                },
                className: 'forecast-col',
                width: 140,
                renderer: (instance, td, row, col, prop, value, cellProperties) => {
                    const brand = tableData[row];
                    if (brand) {
                        const formattedValue = value ? 
                            `${parseFloat(value).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ${brand.currency}` 
                            : 'Hesaplanmadı';
                        
                        td.innerHTML = formattedValue;
                        
                        if (value) {
                            td.style.backgroundColor = '#f6ffed';
                            td.style.color = '#52c41a';
                            td.style.fontWeight = 'bold';
                        } else {
                            td.style.backgroundColor = '#fff2e8';
                            td.style.color = '#fa8c16';
                            td.style.fontStyle = 'italic';
                        }
                    }
                    return td;
                }
            },
            {
                title: `HEDEF CİRO<br/>${dayjs(targetMonthFormatted).format('MMM YYYY')}`,
                data: 'hedefCiro',
                type: 'numeric',
                numericFormat: {
                    pattern: '0,0.00'
                },
                className: 'hedef-col',
                width: 140,
                renderer: (instance, td, row, col, prop, value, cellProperties) => {
                    const brand = tableData[row];
                    if (brand) {
                        const formattedValue = value ? 
                            `${parseFloat(value).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ${brand.currency}` 
                            : 'Hedef Girin';
                        
                        td.innerHTML = formattedValue;
                        
                        if (value) {
                            td.style.backgroundColor = '#fff7e6';
                            td.style.color = '#fa8c16';
                            td.style.fontWeight = 'bold';
                        } else {
                            td.style.backgroundColor = '#fafafa';
                            td.style.color = '#999';
                            td.style.fontStyle = 'italic';
                        }
                    }
                    return td;
                }
            },
            {
                title: `HEDEF ROI<br/>${dayjs(targetMonthFormatted).format('MMM YYYY')}`,
                data: 'hedefROI',
                type: 'numeric',
                numericFormat: {
                    pattern: '0,0.00'
                },
                className: 'hedef-roi-col',
                width: 120,
                renderer: (instance, td, row, col, prop, value, cellProperties) => {
                    const brand = tableData[row];
                    if (brand) {
                        const formattedValue = value ? 
                            parseFloat(value).toFixed(2)
                            : 'ROI Girin';
                        
                        td.innerHTML = formattedValue;
                        
                        if (value) {
                            td.style.backgroundColor = '#f9f0ff';
                            td.style.color = '#722ed1';
                            td.style.fontWeight = 'bold';
                        } else {
                            td.style.backgroundColor = '#fafafa';
                            td.style.color = '#999';
                            td.style.fontStyle = 'italic';
                        }
                    }
                    return td;
                }
            },
            {
                title: 'Son<br/>Hesaplama',
                data: 'lastCalculated',
                readOnly: true,
                className: 'date-col',
                width: 120,
                renderer: (instance, td, row, col, prop, value) => {
                    if (value) {
                        td.innerHTML = dayjs(value).format('DD.MM.YYYY HH:mm');
                        td.style.fontSize = '10px';
                        td.style.color = '#666';
                    } else {
                        td.innerHTML = '-';
                        td.style.color = '#ccc';
                    }
                    return td;
                }
            }
        ];

        return {
            data: tableData,
            columns: columns,
            rowHeaders: true,
            colHeaders: true,
            contextMenu: false,
            manualColumnResize: true,
            manualRowResize: false,
            stretchH: 'all',
            height: 'auto',
            licenseKey: 'non-commercial-and-evaluation',
            language: 'tr-TR',
            afterChange: (changes) => {
                if (changes && changes.length > 0) {
                    changes.forEach(([row, prop, oldValue, newValue]) => {
                        const brand = tableData[row];
                        if (brand && newValue !== null && newValue !== '' && newValue !== oldValue) {
                            if (prop === 'forecastValue') {
                                updateForecastValue(brand.brandId, newValue);
                            } else if (prop === 'hedefCiro') {
                                updateHedefCiro(brand.brandId, newValue, brand.hedefCiroMetricId);
                            } else if (prop === 'hedefROI') {
                                updateHedefROI(brand.brandId, newValue, brand.hedefROIMetricId);
                            }
                        }
                    });
                }
            }
        };
    }, [tableData, targetMonthFormatted]);

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong>
                    Forecast Tablosu - {dayjs(targetMonthFormatted).format('MMMM YYYY')}
                    {selectedTemplate && ` (${selectedTemplate})`}
                </Text>
                
                <Space>
                    <Button
                        icon={<CalculatorOutlined />}
                        onClick={handleBulkCalculate}
                        loading={calculating}
                        type="primary"
                        disabled={tableData.length === 0}
                    >
                        Tümünü Hesapla ({tableData.length})
                    </Button>
                    
                    <Button
                        icon={<SaveOutlined />}
                        onClick={loadTableData}
                        loading={loading}
                    >
                        Yenile
                    </Button>
                </Space>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                    <Spin size="large" tip="Forecast verileri yükleniyor..." />
                </div>
            ) : tableData.length > 0 ? (
                <div className="forecast-table-container">
                    <HotTable
                        ref={hotTableRef}
                        {...hotSettings}
                    />
                    <div style={{ marginTop: 8, fontSize: '11px', color: '#666' }}>
                        💡 Forecast, HEDEF CİRO ve HEDEF ROI değerlerini doğrudan tabloda düzenleyebilirsiniz
                    </div>
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                    {selectedTemplate 
                        ? `${selectedTemplate} template'ine sahip markalar için forecast ayarı bulunamadı`
                        : 'Henüz forecast ayarı olan marka bulunmuyor'
                    }
                </div>
            )}
        </div>
    );
};

export default ForecastTable;